<template>
	<section class="single-idea-articles">
		<div class="wrap">
			<nav class="desktop-nav">
				<div v-if="people.length > 0" class="experts">
					<p>
						Read
						<strong>{{ posts.length }} {{ posts.length === 1 ? 'article' : 'articles' }}</strong> vetted by
					</p>

					<tippy :max-width="show_all_people ? 864 : 400" :interactive="true" :offset="[0, 24]">
						<button type="button" class="btn btn-link">
							<img
								v-for="person in avatars"
								:key="person.id"
								:src="person.image.src"
								:width="person.image.width"
								:height="person.image.height"
								:alt="person.name"
								loading="lazy"
							/>

							<span>+{{ people.length }} {{ people.length === 1 ? 'expert' : 'experts' }}</span>
						</button>

						<template #content>
							<div :class="['people', show_all_people ? 'open' : null]">
								<div v-for="person in truncated_people" :key="person.id" class="person">
									<header>
										<img
											v-if="person.image"
											:src="person.image.src"
											:width="person.image.width"
											:height="person.image.height"
											:alt="person.name"
											loading="lazy"
										/>

										<a :href="person.url">
											{{ person.name }}
										</a>
									</header>

									<p>{{ person.excerpt }}</p>
								</div>
							</div>

							<footer v-if="props.people.length > 4 && !show_all_people">
								<button type="button" class="btn btn-solid" @click="show_all_people = !show_all_people">
									View All
								</button>
							</footer>
						</template>
					</tippy>
				</div>

				<ul class="nav-list">
					<li>
						<button
							type="button"
							:class="['btn', current_category_id === -1 ? 'btn-solid' : 'btn-bordered']"
							@click="current_category_id = -1"
						>
							All
						</button>
					</li>

					<li v-for="category in categories" :key="category.id">
						<button
							type="button"
							:class="['btn', category.id === current_category_id ? 'btn-solid' : 'btn-bordered']"
							@click="current_category_id = category.id"
						>
							{{ category.name }}
						</button>
					</li>
				</ul>
			</nav>

			<div class="right">
				<header>
					<div>
						<h2>Articles</h2>

						<button type="button" class="btn btn-filter btn-solid" @click="show_filters = !show_filters">
							<v-icon slug="bars-filter" />
							<span>Filter</span>
						</button>
					</div>

					<nav v-if="show_filters" class="mobile-nav">
						<ul class="nav-list">
							<li>
								<button
									type="button"
									:class="['btn', current_category_id === -1 ? 'btn-solid' : 'btn-bordered']"
									@click="
										current_category_id = -1;
										show_filters = false;
									"
								>
									All
								</button>
							</li>

							<li v-for="category in categories" :key="category.id">
								<button
									type="button"
									:class="['btn', category.id === current_category_id ? 'btn-solid' : 'btn-bordered']"
									@click="
										current_category_id = category.id;
										show_filters = false;
									"
								>
									{{ category.name }}
								</button>
							</li>
						</ul>
					</nav>
				</header>

				<div class="grid">
					<article v-for="post in truncated_filtered_posts" :key="post.id" class="snippet">
						<img
							v-if="post.image"
							:src="post.image.src"
							:width="post.image.width"
							:height="post.image.height"
							:alt="post.title"
							loading="lazy"
						/>

						<div>
							<div class="snippet-category">
								{{ post.category.name }}
							</div>

							<h4>
								<a :href="post.url">
									{{ post.title }}
								</a>
							</h4>

							<p v-if="post.date" class="snippet-date">
								{{ post.date }}
							</p>
						</div>
					</article>
				</div>

				<footer v-if="filtered_posts.length > per_page && !show_all">
					<button type="button" class="btn btn-solid" @click="show_all = !show_all">View All</button>
				</footer>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.single-idea-articles {
	margin-bottom: $spacer-section;

	> .wrap {
		@include wrap;
		display: grid;
		grid-template-columns: 17.5em 1fr;
		gap: 0 $spacer;
		align-items: flex-start;
	}
}

@include media('<=phone') {
	.single-idea-articles {
		> .wrap {
			padding-left: 0;
			padding-right: 0;
			grid-template-columns: 1fr;
		}
	}
}

.desktop-nav {
	background-color: $white;
	border-radius: $spacer;
	padding: $spacer;
	position: relative;
	z-index: 1;
}

@include media('>phone') {
	.desktop-nav {
		position: sticky;
		top: calc(var(--wp-admin--admin-bar--height, 0px) + 0.25em);
		left: 0;
	}
}

@include media('<=phone') {
	.desktop-nav {
		border-bottom: 1px solid $grey-border;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		padding: $spacer 0.75em;

		ul {
			display: none;
		}
	}
}

.experts {
	background-color: $orange-light;
	padding: 1em;
	border-radius: 1em;
	margin-bottom: 1em;

	> p {
		font-size: rfs(1, 1.125);
		text-wrap: balance;
		margin-bottom: math.div($spacer, 2);

		&:last-child {
			margin-bottom: 0;
		}
	}

	.btn-link {
		font-weight: bold;
		gap: 0;
		text-decoration: underline;

		img {
			border-radius: 50%;
			display: block;
			flex: 0 0 auto;
			border: 1px solid $white;

			+ img {
				margin-left: math.div($spacer, -2);
			}
		}

		span {
			margin-left: math.div($spacer, 2);

			&:first-child {
				margin-left: 0;
			}
		}
	}
}

@include media('<=phone') {
	.experts {
		margin-bottom: 0;

		> p {
			margin-bottom: 1rem;
		}
	}
}

.people {
	display: grid;
	gap: 1em;
	width: 100%;
	grid-template-columns: 1fr 1fr;
	margin-bottom: 1em;
	width: 25em;
	max-width: 100%;

	&.open {
		grid-template-columns: 1fr 1fr 1fr 1fr;
		width: 54em;
	}

	&:last-child {
		margin-bottom: 0;
	}

	.person {
		header {
			display: flex;
			align-items: center;
			gap: math.div($spacer, 2);
			margin-bottom: 0.5em;

			img {
				border-radius: 50%;
				flex: 0 0 auto;
			}

			a {
				font-weight: bold;
				color: $black;
				text-decoration: underline;
			}
		}

		> p {
			font-size: 0.875rem;
			line-height: 1.25;
		}
	}

	+ footer {
		display: flex;
		justify-content: center;

		.btn-solid {
			font-size: 1em;
			padding-top: math.div($spacer, 2);
			padding-bottom: math.div($spacer, 2);
		}
	}
}

@include media('<=portrait') {
	.people {
		&.open {
			grid-template-columns: 1fr 1fr;
		}
	}
}

@include media('<=phone') {
	.people {
		grid-template-columns: 1fr;

		&.open {
			grid-template-columns: 1fr;
		}
	}
}

.nav-list {
	li {
		margin-bottom: math.div($spacer, 2);

		&:last-child {
			margin-bottom: 0;
		}

		.btn {
			width: 100%;
			font-size: 1em;
			padding: math.div($spacer, 2);
		}
	}
}

.right {
	background-color: $white;
	border-radius: $spacer;
	padding: $spacer-block;
}

@include media('<=phone') {
	.right {
		border-top-left-radius: 0;
		border-top-right-radius: 0;
		padding: $spacer 0.75em;
	}
}

header {
	position: relative;
	margin-bottom: $spacer;

	> div {
		display: flex;
		align-items: center;
		gap: $spacer-block;
	}

	h2 {
		margin-bottom: 0;
	}

	.btn-filter {
		margin-left: auto;
		flex: 0 0 auto;
		background-color: $orange-light;
		color: $black;
		font-weight: normal;
		font-size: 1em;
		padding: 0.5em 1em;
		display: inline-flex;
		align-items: center;
		gap: ($spacer * 0.5);

		svg {
			flex: 0 0 auto;
			width: 1.25em;
			height: 1.25em;
		}
	}

	.nav-list {
		position: absolute;
		top: 100%;
		right: 0;
		z-index: 1;
		background-color: $orange-light;
		border-radius: 1em;
		padding: 1em;
	}
}

@include media('>phone') {
	header {
		.btn-filter,
		.mobile-nav {
			display: none;
		}
	}
}

.grid {
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	border-left: 1px solid $grey-border;
	margin-bottom: $spacer;

	&:last-child {
		margin-bottom: 0;
	}
}

@include media('<=portrait') {
	.grid {
		grid-template-columns: 1fr 1fr;
	}
}

.snippet {
	padding: 1em;
	border: none;
	background-color: transparent;
	border-right: 1px solid $grey-border;
	border-bottom: 1px solid $grey-border;
	border-radius: 0;

	&:nth-child(1),
	&:nth-child(2),
	&:nth-child(3) {
		border-top: 1px solid $grey-border;
	}

	img {
		margin-bottom: 1em;
	}

	h4 {
		@include h5;
	}
}

@include media('<=portrait') {
	.snippet {
		&:nth-child(3) {
			border-top: none;
		}
	}
}

footer {
	display: flex;
	justify-content: center;
}

@include media('<=phone') {
	footer {
		.btn-solid {
			width: 100%;
		}
	}
}
</style>

<script setup>
import { computed, ref } from 'vue';
import { Tippy } from 'vue-tippy';

import VIcon from '../jobs-app/_v-icon.vue';

const props = defineProps({
	posts: {
		type: Array,
		required: true,
	},
	people: {
		type: Array,
		required: true,
	},
	categories: {
		type: Array,
		required: true,
	},
});

const per_page = ref(12);

const current_category_id = ref(-1);

const show_all = ref(false);

const show_filters = ref(false);

const show_all_people = ref(false);

const truncated_people = computed(() => {
	if (show_all_people.value) {
		return props.people;
	}

	return props.people.slice(0, 4);
});

const filtered_posts = computed(() => {
	if (current_category_id.value === -1) {
		return props.posts;
	}

	return props.posts.filter((post) => post.category.id === current_category_id.value);
});

const truncated_filtered_posts = computed(() => {
	if (show_all.value) {
		return filtered_posts.value;
	}

	return filtered_posts.value.slice(0, per_page.value);
});

const avatars = computed(() => {
	const people = props.people.filter((person) => Boolean(person.image));

	return people.slice(0, 3);
});
</script>
